// src/services/adminService.js
import api from '@/core/api/api';
import { ROUTES } from '@/core/constants/routes';

/** Path for `dev_maintain_lookups` Lambda (CRUD lookup DynamoDB rows). */
const MANAGE_LOOKUPS = ROUTES.API.ADMIN.MANAGE_LOOKUPS;

function normalizeLookupValueForApi(value) {
  return {
    value: String(value.value ?? '').trim(),
    display: String(value.display ?? '').trim(),
    description: String(value.description ?? '').trim(),
    active: Boolean(value.active),
    order: typeof value.order === 'number' ? value.order : Number(value.order) || 0,
    ...(value.color ? { color: String(value.color) } : {}),
    ...(value.color_rgb ? { color_rgb: String(value.color_rgb) } : {}),
    ...(value.color_hsl ? { color_hsl: String(value.color_hsl) } : {}),
    associations:
      value.associations && typeof value.associations === 'object' ? value.associations : {},
  };
}

/**
 * Convert a human-readable display name to a lowercase underscore-separated value code.
 * e.g. "United States" → "united_states", "North America" → "north_america"
 */
function displayToValue(display) {
  return String(display)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Generate a deterministic HSL color from a value string.
 * Produces a distinct hue per value; saturation and lightness are fixed for visual consistency.
 */
function generateValueColor(value) {
  let hash = 0;
  const str = String(value);
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  const hue = hash % 360;
  return `hsl(${hue}, 60%, 42%)`;
}

const adminService = {
  // ===== DASHBOARD =====

  /**
   * Aggregated admin home dashboard — pending actions, recent activity, counts.
   * @returns {Promise<Object>}
   */
  getDashboardSummary: async () => {
    try {
      const response = await api.get('/admin/dashboard/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin dashboard summary:', error);
      throw error;
    }
  },

  /**
   * Manually trigger the executive daily brief email.
   * @param {{ reportDate?: string, dryRun?: boolean, forceSend?: boolean }} options
   */
  sendDailyDigest: async ({ reportDate, dryRun = true, forceSend = false, audience } = {}) => {
    try {
      const params = {};
      if (reportDate) params.report_date = reportDate;
      if (dryRun) params.dry_run = 'true';
      if (forceSend) params.force_send = 'true';
      if (audience) params.audience = audience;
      const response = await api.post('/admin/daily-digest/send', null, { params });
      return response.data;
    } catch (error) {
      console.error('Error sending daily digest:', error);
      throw error;
    }
  },

  /**
   * On-demand AI executive briefing with opportunity deep-dives.
   * @param {{ reportDate?: string, opportunityScope?: string, coInvestIds?: string[] }} options
   */
  generateExecutiveBrief: async ({ reportDate, report_date } = {}) => {
    try {
      const body = {};
      const dateVal = report_date || reportDate;
      if (dateVal) body.report_date = dateVal;
      const response = await api.post('/admin/executive-brief/generate', body);
      return response.data;
    } catch (error) {
      console.error('Error starting executive brief generation:', error);
      throw error;
    }
  },

  getExecutiveBriefLatest: async () => {
    try {
      const response = await api.get('/admin/executive-brief/latest');
      return response.data;
    } catch (error) {
      console.error('Error fetching latest executive brief:', error);
      throw error;
    }
  },

  /**
   * Poll async executive brief generation job status.
   * @param {string} jobId
   */
  getExecutiveBriefStatus: async (jobId) => {
    try {
      const response = await api.get(`/admin/executive-brief/status/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching executive brief status:', error);
      throw error;
    }
  },

  /**
   * Email a reviewed executive briefing to custom recipients.
   */
  sendExecutiveBrief: async ({
    recipients,
    subject,
    html_body,
    text_body,
    narrative,
    dryRun = false,
    forceSend = false,
  }) => {
    try {
      const response = await api.post('/admin/executive-brief/send', {
        recipients,
        subject,
        html_body,
        text_body,
        narrative,
        dry_run: dryRun,
        force_send: forceSend,
      });
      return response.data;
    } catch (error) {
      console.error('Error sending executive brief:', error);
      throw error;
    }
  },

  // ===== PLATFORM FEATURES =====
  
  /**
   * Get current platform feature settings
   * @param {string} adminUserId - Admin user ID (optional - if not provided, returns only global settings)
   */
  getPlatformFeatures: async (adminUserId, options = {}) => {
    try {
      const params = adminUserId ? { admin_user_id: adminUserId } : {};
      if (options.includeManagers) {
        params.include_managers = 'true';
      }
      const response = await api.get('/admin/platform_features', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching platform features:', error);
      throw error;
    }
  },

  /**
   * Update platform features for all managers
   * @param {string} featureName - Feature name to update
   * @param {boolean} featureValue - Feature value (true/false)
   * @param {string} adminUserId - Admin user ID
   */
  updatePlatformFeature: async (featureName, featureValue, adminUserId, managerIds = null) => {
    try {
      const body = {
        feature_name: featureName,
        feature_value: featureValue,
        admin_user_id: adminUserId
      };
      if (managerIds && managerIds.length > 0) {
        body.manager_ids = managerIds;
      }
      const response = await api.post('/admin/platform_features', body);
      return response.data;
    } catch (error) {
      console.error('Error updating platform feature:', error);
      throw error;
    }
  },

  /**
   * Update manager dashboard metrics visibility for all managers
   * @param {boolean} enabled - Whether to show metrics
   * @param {string} adminUserId - Admin user ID
   */
  updateManagerDashboardMetrics: async (enabled, adminUserId) => {
    return adminService.updatePlatformFeature('show_dashboard_metrics', enabled, adminUserId);
  },

  // ===== NOTIFICATION CONTROLS =====
  
  /**
   * Update manager view notifications for all managers
   * @param {boolean} enabled - Whether to enable view notifications
   * @param {string} adminUserId - Admin user ID
   */
  updateManagerViewNotifications: async (enabled, adminUserId) => {
    return adminService.updatePlatformFeature('enable_manager_view_notifications', enabled, adminUserId);
  },

  /**
   * Update manager watchlist notifications for all managers
   * @param {boolean} enabled - Whether to enable watchlist notifications
   * @param {string} adminUserId - Admin user ID
   */
  updateManagerWatchlistNotifications: async (enabled, adminUserId) => {
    return adminService.updatePlatformFeature('enable_manager_watchlist_notifications', enabled, adminUserId);
  },

  /**
   * Update manager download notifications for all managers
   * @param {boolean} enabled - Whether to enable download notifications
   * @param {string} adminUserId - Admin user ID
   */
  updateManagerDownloadNotifications: async (enabled, adminUserId) => {
    return adminService.updatePlatformFeature('enable_manager_download_notifications', enabled, adminUserId);
  },

  /**
   * Update manager closing notifications for all managers
   * @param {boolean} enabled - Whether to enable closing notifications
   * @param {string} adminUserId - Admin user ID
   */
  updateManagerClosingNotifications: async (enabled, adminUserId) => {
    return adminService.updatePlatformFeature('enable_manager_closing_notifications', enabled, adminUserId);
  },

  // ===== GLOBAL PLATFORM SETTINGS =====

  /**
   * Update agreement acceptance requirement (global setting)
   * @param {boolean} enabled - Whether to require agreement acceptance
   * @param {string} adminUserId - Admin user ID
   */
  updateAgreementAcceptanceRequirement: async (enabled, adminUserId) => {
    return adminService.updatePlatformFeature('require_agreement_acceptance', enabled, adminUserId);
  },

  // ===== USER APPROVAL MANAGEMENT =====

  /**
   * Pending registration queue (managers + investors). Optional role: all | manager | investor
   * @param {string} adminUserId
   * @param {'all'|'manager'|'investor'} [role='all']
   */
  getPendingUsers: async (adminUserId, role = 'all') => {
    try {
      const params = {
        action: 'get_pending_users',
        admin_user_id: adminUserId,
      };
      if (role && role !== 'all') {
        params.role = role;
      }
      const response = await api.get('/entities/user/management', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching pending users:', error);
      throw error;
    }
  },

  /**
   * Enriched portal access tracker (paginated). Admin only.
   * @param {string} adminUserId
   * @param {{ role?: string, status?: string, cursor?: string, includeCountersign?: boolean }} [opts]
   * @returns {Promise<{ success, users, count, next_cursor, has_more }>}
   */
  getPortalAccessUsers: async (adminUserId, opts = {}) => {
    try {
      const params = {
        action: 'list_portal_access_users',
        admin_user_id: adminUserId,
      };
      if (opts.role && opts.role !== 'all') {
        params.role = opts.role;
      }
      if (opts.status) {
        params.status = opts.status;
      }
      if (opts.cursor) {
        params.cursor = opts.cursor;
      }
      if (opts.includeCountersign === false) {
        params.include_countersign = 'false';
      }
      const response = await api.get('/entities/user/management', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching portal access users:', error);
      throw error;
    }
  },

  /**
   * @deprecated Use getPendingUsers(adminUserId, 'all')
   */
  getPendingManagers: async (adminUserId) => {
    return adminService.getPendingUsers(adminUserId, 'all');
  },

  /**
   * Approve a pending user (manager or investor)
   */
  approveUser: async (adminUserId, userId) => {
    try {
      const response = await api.post('/entities/user/management', {
        action: 'approve_user',
        admin_user_id: adminUserId,
        user_id: userId
      });
      return response.data;
    } catch (error) {
      console.error('Error approving user:', error);
      throw error;
    }
  },

  /** @deprecated Use approveUser */
  approveManager: async (adminUserId, userId) => {
    return adminService.approveUser(adminUserId, userId);
  },

  declineUser: async (adminUserId, userId, declineReason = '') => {
    try {
      const response = await api.post('/entities/user/management', {
        action: 'decline_user',
        admin_user_id: adminUserId,
        user_id: userId,
        decline_reason: declineReason
      });
      return response.data;
    } catch (error) {
      console.error('Error declining user:', error);
      throw error;
    }
  },

  /** @deprecated Use declineUser */
  declineManager: async (adminUserId, userId, declineReason = '') => {
    return adminService.declineUser(adminUserId, userId, declineReason);
  },

  /**
   * Update user approval status
   * @param {string} adminUserId - Admin user ID
   * @param {string} userId - User ID to update
   * @param {string} status - New status (pending, approved, declined, suspended)
   */
  updateUserStatus: async (adminUserId, userId, status) => {
    try {
      const response = await api.put('/entities/user/management', {
        action: 'update_status',
        admin_user_id: adminUserId,
        user_id: userId,
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  },

  /**
   * Get list of all users with optional filters
   * @param {string} adminUserId - Admin user ID
   * @param {Object} filters - Optional filters {role: 'manager', status: 'pending', limit: 100}
   */
  getAllUsers: async (adminUserId, filters = {}) => {
    try {
      const params = {
        action: 'list_all_users',
        admin_user_id: adminUserId,
        ...filters
      };
      const response = await api.get('/entities/user/management', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  },

  /**
   * Check specific user's approval and active status
   * @param {string} userId - User ID to check
   */
  getUserStatus: async (userId) => {
    try {
      const response = await api.get('/entities/user/management', {
        params: { 
          action: 'get_user_status',
          user_id: userId 
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error checking user status:', error);
      throw error;
    }
  },

  // ===== MANAGER MANAGEMENT =====
  
  /**
   * Get all firms for admin selection
   * @param {Object} filters - Optional filters
   */
  getFirms: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.limit) {
        queryParams.append('limit', filters.limit.toString());
      }
      // Always include basic info for admin dropdowns
      queryParams.append('include_basic', 'true');
      
      const response = await api.get(`/fetch/firms?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching firms:', error);
      throw error;
    }
  },

  /**
   * Add a new manager to an existing firm
   * @param {Object} managerData - Manager information
   * @param {string} adminUserId - Admin user ID
   */
  addManagerToFirm: async (managerData, adminUserId) => {
    try {
      const response = await api.post('/admin/add_manager_to_firm', {
        ...managerData,
        admin_user_id: adminUserId
      });
      return response.data;
    } catch (error) {
      console.error('Error adding manager to firm:', error);
      throw error;
    }
  },

  // ===== BULK UPLOAD OPERATIONS =====
  
  /**
   * Upload bulk firms data file
   * @param {Object} uploadData - File upload data
   * @param {string} adminUserId - Admin user ID
   */
  uploadBulkFirms: async (uploadData, adminUserId) => {
    try {
      const response = await api.post('/manage_docs/upload/upload_bulk_firms', {
        ...uploadData,
        user_id: adminUserId
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading bulk firms file:', error);
      throw error;
    }
  },

  /**
   * Get bulk upload processing status
   * @param {string} documentId - Document ID from upload
   */
  getBulkUploadStatus: async (documentId) => {
    try {
      const response = await api.get(`/manage_docs/document_status/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting bulk upload status:', error);
      throw error;
    }
  },

  // ===== ADMIN PREFERENCES =====
  
  /**
   * Get admin preferences
   * @param {string} adminId - Admin user ID
   */
  getCommunicationSettings: async (adminId) => {
    try {
      const response = await api.get('/admin/settings/communication', {
        params: { admin_id: adminId },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching communication settings:', error);
      throw error;
    }
  },

  updateCommunicationSettings: async (adminId, payload) => {
    try {
      const response = await api.post('/admin/settings/communication', {
        admin_id: adminId,
        ...payload,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating communication settings:', error);
      throw error;
    }
  },

  getPreferences: async (adminId) => {
    try {
      const response = await api.get('/admin/settings/preferences', {
        params: { admin_id: adminId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin preferences:', error);
      throw error;
    }
  },

  /**
   * Update admin preferences
   * @param {string} adminId - Admin user ID
   * @param {Object} preferences - Preference object to update
   */
  updatePreferences: async (adminId, preferences) => {
    try {
      const response = await api.post('/admin/settings/preferences', {
        admin_id: adminId,
        admin_preference: preferences
      });
      return response.data;
    } catch (error) {
      console.error('Error updating admin preferences:', error);
      throw error;
    }
  },

  // ===== DUAL SIGNATURE CONFIG =====

  /**
   * Get the platform-wide dual-signature config (approver email + platform URL).
   * @returns {Promise<{ default_approver_email: string, platform_url: string }>}
   */
  getDualSignatureConfig: async () => {
    const response = await api.get('/admin/settings/dual-signature-config');
    return response.data;
  },

  /**
   * Update the platform-wide dual-signature config.
   * Pass only the fields you want to change.
   * @param {{ default_approver_email?: string, platform_url?: string }} config
   * @returns {Promise<{ message: string }>}
   */
  updateDualSignatureConfig: async (config) => {
    const response = await api.post('/admin/settings/dual-signature-config', config);
    return response.data;
  },

  // ===== ADMIN UTILITIES =====

  /**
   * Get list of available admin utilities
   */
  getUtilities: async () => {
    try {
      const response = await api.get('/admin/utilities');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin utilities:', error);
      throw error;
    }
  },

  /**
   * List managers who have opportunities and all investors (no passwords).
   * Returns login emails (cognito_username) and contact emails.
   * @param {string} adminUserId - Admin user ID
   */
  listUsersWithOpportunities: async (adminUserId) => {
    try {
      const response = await api.get('/admin/utilities/list-users', {
        params: { admin_user_id: adminUserId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching user list:', error);
      throw error;
    }
  },

  /**
   * Paginated manager directory for admin.
   * @param {string} adminUserId
   * @param {{ cursor?: string, status?: string, hasOpportunities?: boolean }} [opts]
   */
  listManagers: async (adminUserId, opts = {}) => {
    try {
      const params = { admin_user_id: adminUserId };
      if (opts.cursor) params.cursor = opts.cursor;
      if (opts.status) params.status = opts.status;
      if (opts.actionable === true) params.actionable = 'true';
      if (opts.hasOpportunities === true) params.has_opportunities = 'true';
      if (opts.hasOpportunities === false) params.has_opportunities = 'false';
      if (opts.segment) params.segment = opts.segment;
      const response = await api.get('/admin/managers', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching manager directory:', error);
      throw error;
    }
  },

  /**
   * Opportunities for a manager (includes firm-shared opps when firm_id provided).
   * @param {string} adminUserId
   * @param {string} managerId
   * @param {string} [firmId]
   */
  getManagerOpportunities: async (adminUserId, managerId, firmId) => {
    try {
      const params = { admin_user_id: adminUserId, manager_id: managerId };
      if (firmId) params.firm_id = firmId;
      const response = await api.get('/admin/managers/opportunities', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching manager opportunities:', error);
      throw error;
    }
  },

  /**
   * Paginated allocator (investor) directory for admin.
   * @param {string} adminUserId
   * @param {{ cursor?: string, status?: string }} [opts]
   */
  listAllocators: async (adminUserId, opts = {}) => {
    try {
      const params = { admin_user_id: adminUserId };
      if (opts.cursor) params.cursor = opts.cursor;
      if (opts.status) params.status = opts.status;
      if (opts.actionable === true) params.actionable = 'true';
      const response = await api.get('/admin/allocators', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching allocator directory:', error);
      throw error;
    }
  },

  /**
   * Paginated platform opportunity directory for admin.
   * @param {string} adminUserId
   * @param {{ cursor?: string, status?: string, visible?: boolean }} [opts]
   */
  listOpportunities: async (adminUserId, opts = {}) => {
    try {
      const params = { admin_user_id: adminUserId };
      if (opts.cursor) params.cursor = opts.cursor;
      if (opts.status) params.status = opts.status;
      if (opts.visible === true) params.visible = 'true';
      if (opts.visible === false) params.visible = 'false';
      const response = await api.get('/admin/opportunities', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching opportunity directory:', error);
      throw error;
    }
  },

  /**
   * Generate deal sheets (AI opportunity profiles) for every opportunity.
   * Dry run: sync response with counts. Live: 202 + accepted flag; worker dispatches jobs (avoids API timeout).
   * @param {string} adminUserId - Admin user ID
   * @param {boolean} dryRun - If true, scan and count only (no invocations fired)
   */
  generateAllDealSheets: async (adminUserId, dryRun = true) => {
    try {
      const response = await api.post('/admin/utilities/generate-deal-sheets', {
        admin_user_id: adminUserId,
        dry_run: dryRun
      });
      return response.data;
    } catch (error) {
      console.error('Error running generate-deal-sheets utility:', error);
      throw error;
    }
  },

  /**
   * Import opportunities from an Excel / CSV file.
   * Dry-run returns a full analysis; live import fires asynchronously and returns immediately.
   * @param {Object} payload
   * @param {string}  payload.admin_user_id       - Admin user ID
   * @param {boolean} payload.dry_run             - true = analyse only, false = create data
   * @param {string}  payload.file_base64         - Base64-encoded file contents
   * @param {string}  payload.file_name           - Original filename (.xlsx or .csv)
   * @param {string}  [payload.source]            - 'auto' | 'yfinance' | 'bloomberg'
   * @param {string}  [payload.bloomberg_api_key] - Ephemeral Bloomberg key (never stored)
   * @param {boolean} [payload.clear_before_import] - Delete existing opportunities first
   */
  importOpportunities: async (payload) => {
    try {
      const response = await api.post('/admin/utilities/import-opportunities', payload);
      return response.data;
    } catch (error) {
      console.error('Error running import-opportunities utility:', error);
      throw error;
    }
  },

  /**
   * Poll the status of a live import job.
   * Returns processing_status, progress fields, and (when done) import_report.
   * @param {string} documentId - document_id returned from importOpportunities
   */
  getImportStatus: async (documentId) => {
    try {
      const response = await api.get(`/admin/utilities/import-status/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error polling import status:', error);
      throw error;
    }
  },

  /**
   * Publish all opportunities — sets visible=true and investor_types=['all'] for every record.
   * @param {string} adminUserId - Admin user ID
   * @param {boolean} dryRun - If true, scan and count only (no writes)
   */
  publishAllOpportunities: async (adminUserId, dryRun = true) => {
    try {
      const response = await api.post('/admin/utilities/publish-opport', {
        admin_user_id: adminUserId,
        dry_run: dryRun
      });
      return response.data;
    } catch (error) {
      console.error('Error running publish-all-opportunities utility:', error);
      throw error;
    }
  },

  // ===== INDEX BUILDER =====

  /**
   * Get filtered opportunities for index building
   * @param {Object} filters - Filter criteria
   * @returns {Promise} API response with opportunities list
   */
  getIndexOpportunities: async (filters) => {
    try {
      const response = await api.get('/admin/index/opportunities', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching index opportunities:', error);
      throw error;
    }
  },

  /**
   * Start index analysis (async - returns document_id immediately)
   * This will take 20-30 seconds in the background
   * @param {Object} analysisRequest - Analysis request with admin_user_id, index_name, selected_opportunities
   * @returns {Promise} API response with document_id for polling
   */
  analyzeIndex: async (analysisRequest) => {
    try {
      const response = await api.post('/admin/index/analyze', analysisRequest);
      return response.data;  // { status: 'processing_started', document_id: '...' }
    } catch (error) {
      console.error('Error starting index analysis:', error);
      throw error;
    }
  },

  /**
   * Poll index analysis status
   * Call this repeatedly until status is 'completed' or 'failed'
   * @param {string} documentId - Document ID returned from analyzeIndex
   * @returns {Promise} API response with processing_status
   */
  getIndexAnalysisStatus: async (documentId) => {
    try {
      const response = await api.get(`/admin/index/status/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching analysis status:', error);
      throw error;
    }
  },

  /**
   * Get index analysis recommendations (when completed)
   * @param {string} documentId - Document ID from completed analysis
   * @returns {Promise} API response with 6 index recommendations
   */
  getIndexAnalysisResults: async (documentId) => {
    try {
      const response = await api.get(`/admin/index/results/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching analysis results:', error);
      throw error;
    }
  },

  /**
   * Save selected index as investable opportunity
   * @param {Object} indexData - Index data to save
   * @returns {Promise} API response with index_id
   */
  saveIndex: async (indexData) => {
    try {
      const response = await api.post('/admin/index/save', indexData);
      return response.data;
    } catch (error) {
      console.error('Error saving index:', error);
      throw error;
    }
  },

  /**
   * Get list of created indices
   * @param {string} adminUserId - Admin user ID (optional)
   * @returns {Promise} API response with indices list
   */
  getIndices: async (adminUserId) => {
    try {
      const params = adminUserId ? { admin_user_id: adminUserId } : {};
      const response = await api.get('/admin/index/list', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching indices:', error);
      throw error;
    }
  },

  /**
   * Get detailed index information
   * @param {string} indexId - Index ID
   * @returns {Promise} API response with index details
   */
  getIndexDetails: async (indexId) => {
    try {
      const response = await api.get(`/admin/index/${indexId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching index details:', error);
      throw error;
    }
  },

  /**
   * Archive/delete an index
   * @param {string} indexId - Index ID
   * @param {string} adminUserId - Admin user ID
   * @returns {Promise} API response
   */
  deleteIndex: async (indexId, adminUserId) => {
    try {
      const response = await api.delete(`/admin/index/${indexId}`, {
        params: { admin_user_id: adminUserId }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting index:', error);
      throw error;
    }
  },

  /**
   * Archive an index (soft delete)
   * @param {string} indexId - Index ID
   * @param {string} adminUserId - Admin user ID
   * @returns {Promise} API response
   */
  archiveIndex: async (indexId, adminUserId) => {
    try {
      const response = await api.delete(`/admin/index/${indexId}`, {
        params: { admin_user_id: adminUserId }
      });
      return response.data;
    } catch (error) {
      console.error('Error archiving index:', error);
      throw error;
    }
  },

  // ===== PERFORMANCE METRICS =====

  /**
   * Start performance metrics calculation (fire-and-forget)
   * @param {string} adminUserId - Admin user ID
   * @returns {Promise} API response with document_id for polling
   */
  startPerformanceMetricsCalculation: async (adminUserId) => {
    try {
      const response = await api.post('/admin/performance_metrics/calculate', {
        user_id: adminUserId
      });
      return response.data;
    } catch (error) {
      console.error('Error starting performance metrics calculation:', error);
      throw error;
    }
  },

  /**
   * Poll calculation status
   * @param {string} documentId - Document ID from calculation start
   * @returns {Promise} API response with status and progress
   */
  getPerformanceMetricsStatus: async (documentId) => {
    try {
      const response = await api.get(`/admin/performance_metrics/status/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching metrics status:', error);
      throw error;
    }
  },

  /**
   * Get latest calculated metrics
   * @returns {Promise} API response with co_investment_index and firm_index arrays
   */
  getLatestPerformanceMetrics: async () => {
    try {
      const response = await api.get('/admin/performance_metrics/latest');
      return response.data;
    } catch (error) {
      console.error('Error fetching latest metrics:', error);
      throw error;
    }
  },

  /**
   * Get detailed year performance metrics for drill-down
   * @param {string} indexType - 'opportunity' or 'firm'
   * @param {number} year - Year to retrieve
   * @returns {Promise} API response with monthly data, constituents, and annual metrics
   */
  getPerformanceMetricsYear: async (indexType, year) => {
    try {
      // Map frontend indexType to backend metrics_type
      const metricsType = indexType === 'opportunity' ? 'opportunity' : 'firm';
      
      const response = await api.get('/admin/performance_metrics/year', {
        params: {
          metrics_type: metricsType,
          year: year
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching year metrics:', error);
      throw error;
    }
  },

  /**
   * Delete year performance metrics
   * @param {string} indexType - 'opportunity' or 'firm'
   * @param {number} year - Year to delete
   * @param {string} adminUserId - Admin user ID
   * @returns {Promise} API response
   */
  deletePerformanceMetricsYear: async (indexType, year, adminUserId) => {
    try {
      const metricsType = indexType === 'co_investment' ? 'opportunity' : 'firm';
      
      const response = await api.delete('/admin/performance_metrics/year', {
        params: {
          metrics_type: metricsType,
          year: year,
          admin_user_id: adminUserId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting year metrics:', error);
      throw error;
    }
  },

  /**
   * Get opportunities for a specific firm
   * @param {string} firmId - Firm ID
   * @returns {Promise} List of opportunities/entities for the firm
   */
  getFirmOpportunities: async (firmId) => {
    try {
      const response = await api.get('/performance/entities', {
        params: {
          firm_id: firmId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching firm opportunities:', error);
      throw error;
    }
  },

  /**
   * Delete an opportunity (admin only — full cascade delete).
   * Removes performance data, master record, and narrative record.
   * @param {string} opportunityId - co_invest_id of the opportunity to delete
   * @param {string} adminUserId   - Admin user ID (validated server-side)
   * @returns {Promise<{ success: boolean, message: string, deleted_items: string[] }>}
   */
  deleteOpportunity: async (opportunityId, adminUserId) => {
    try {
      const response = await api.delete('/managers/maintain_opportunities', {
        data: {
          co_invest_id: opportunityId,
          user_id: adminUserId,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      throw error;
    }
  },

  /**
   * Platform-suppress or unsuppress a firm, institution, or user.
   * @param {{ id: string, type: 'firm'|'institution'|'user', suppressed: boolean, reason?: string }} payload
   */
  setPlatformSuppression: async ({ id, type, suppressed, reason = '' }, adminUserId) => {
    const response = await api.put(
      '/admin/platform-suppression',
      { id, type, suppressed, reason },
      { params: { admin_user_id: adminUserId } }
    );
    return response.data;
  },

  /**
   * Legacy firm suppress — forwards to unified API.
   */
  setFirmPlatformSuppression: async (firmId, { suppressed, reason = '' }, adminUserId) => {
    return adminService.setPlatformSuppression(
      { id: firmId, type: 'firm', suppressed, reason },
      adminUserId
    );
  },

  getInstitutions: async (adminUserId, { cursor } = {}) => {
    const params = { admin_user_id: adminUserId };
    if (cursor) params.cursor = cursor;
    const response = await api.get('/admin/institutions', { params });
    return response.data;
  },

  /**
   * Platform-suppress or unsuppress a single opportunity.
   */
  setOpportunityPlatformSuppression: async (coInvestId, { suppressed, reason = '' }, adminUserId) => {
    const response = await api.put(
      `/admin/opportunities/${encodeURIComponent(coInvestId)}/platform-suppression`,
      { suppressed, reason },
      { params: { admin_user_id: adminUserId } }
    );
    return response.data;
  },

  setOpportunityExecutiveBriefExclusion: async (coInvestId, { excluded, reason = '' }, adminUserId) => {
    const response = await api.put(
      `/admin/opportunities/${encodeURIComponent(coInvestId)}/executive-brief-exclusion`,
      { excluded, reason },
      { params: { admin_user_id: adminUserId } }
    );
    return response.data;
  },

  /**
   * Start a Deal Sheet Book job — backend generates a firm-anonymized LLM cover PDF
   * for the selected opportunities (fire-and-forget; poll with getDealSheetBookStatus).
   * @param {string[]} coInvestIds
   * @param {string} adminUserId
   * @returns {Promise<{ job_id: string, status: string, total: number }>}
   */
  startDealSheetBook: async (coInvestIds, adminUserId) => {
    const response = await api.post('/admin/opportunities/deal-sheet-book', {
      co_invest_ids: coInvestIds,
      admin_user_id: adminUserId,
    });
    return response.data;
  },

  /**
   * Poll a Deal Sheet Book job. On completion returns presigned `cover_url` (PDF)
   * and `report_url` (standalone visual HTML report) plus `included` / `unavailable`
   * co_invest_id lists.
   * @param {string} jobId
   */
  getDealSheetBookStatus: async (jobId) => {
    const response = await api.get(
      `/admin/opportunities/deal-sheet-book/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  /**
   * List opportunities for a firm (admin directory shape with suppression flags).
   */
  getFirmOpportunitiesAdmin: async (firmId, adminUserId) => {
    const response = await api.get('/admin/managers/opportunities', {
      params: {
        firm_id: firmId,
        admin_user_id: adminUserId,
      },
    });
    return response.data;
  },

  // ===== REVEAL REQUESTS =====

  /**
   * Get investor reveal requests (express interest).
   * @param {string} statusFilter - 'PENDING' | 'ADMIN_REVIEW' | 'REVEALED' | 'DECLINED' | 'all'
   * @param {string} adminUserId  - Admin user ID passed as fallback when authorizer context is absent
   */
  getRevealRequests: async (statusFilter = 'all', adminUserId) => {
    try {
      const params = { status: statusFilter };
      if (adminUserId) params.user_id = adminUserId;
      const response = await api.get('/admin/reveal-requests', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching reveal requests:', error);
      throw error;
    }
  },

  /**
   * Approve a reveal request — grants the investor access to the full opportunity.
   * @param {string} requestId
   * @param {string} adminNotes
   * @param {string} adminUserId
   */
  approveRevealRequest: async (requestId, adminNotes = '', adminUserId) => {
    try {
      const response = await api.post(`/admin/reveal-requests/${requestId}/reveal`, {
        admin_notes: adminNotes,
        ...(adminUserId ? { user_id: adminUserId } : {})
      });
      return response.data;
    } catch (error) {
      console.error('Error approving reveal request:', error);
      throw error;
    }
  },

  /**
   * Decline a reveal request.
   * @param {string} requestId
   * @param {string} adminNotes
   * @param {string} adminUserId
   */
  declineRevealRequest: async (requestId, adminNotes = '', adminUserId) => {
    try {
      const response = await api.post(`/admin/reveal-requests/${requestId}/decline`, {
        admin_notes: adminNotes,
        ...(adminUserId ? { user_id: adminUserId } : {})
      });
      return response.data;
    } catch (error) {
      console.error('Error declining reveal request:', error);
      throw error;
    }
  },

  /**
   * Opportunity content approvals — manager-created deals pending platform publish review.
   * @param {string} statusFilter - pending | approved | declined | all
   * @param {string} adminUserId
   */
  getOpportunityContentApprovals: async (statusFilter = 'pending', adminUserId) => {
    try {
      const params = { status: statusFilter };
      if (adminUserId) params.user_id = adminUserId;
      const response = await api.get('/admin/opportunity-content-approvals', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching opportunity content approvals:', error);
      throw error;
    }
  },

  getOpportunityContentApprovalDetail: async (coInvestId, adminUserId) => {
    try {
      const params = { co_invest_id: coInvestId };
      if (adminUserId) params.user_id = adminUserId;
      const response = await api.get('/admin/opportunity-content-approvals/detail', { params });
      return response.data;
    } catch (error) {
      console.error('Error loading opportunity approval detail:', error);
      throw error;
    }
  },

  approveOpportunityContent: async (coInvestId, adminUserId) => {
    try {
      const response = await api.post(
        `/admin/opportunity-content-approvals/${coInvestId}/approve`,
        {
          ...(adminUserId ? { user_id: adminUserId } : {})
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error approving opportunity content:', error);
      throw error;
    }
  },

  declineOpportunityContent: async (coInvestId, adminNotes = '', adminUserId) => {
    try {
      const response = await api.post(
        `/admin/opportunity-content-approvals/${coInvestId}/decline`,
        {
          admin_notes: adminNotes,
          ...(adminUserId ? { user_id: adminUserId } : {})
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error declining opportunity content:', error);
      throw error;
    }
  },

  setOpportunityProductType: async (coInvestId, productType, adminUserId) => {
    try {
      const response = await api.post(
        `/admin/opportunity-content-approvals/${coInvestId}/product-type`,
        {
          product_type: productType,
          ...(adminUserId ? { user_id: adminUserId } : {}),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error setting opportunity product type:', error);
      throw error;
    }
  },

  // ===== FIRM DATA ENRICHMENT UTILITY (/admin/utilities/firm-upload) =====

  /**
   * Firm Data Enrichment — all actions go through POST /admin/utilities/firm-upload
   * (API Gateway path name is historical; this is not “upload only”).
   *
   * Sync examples: load_firm_context, create_workspace, get_workspace, get_upload_url,
   * match_opportunities, update_field, …
   * Async: extract_source, commit (poll getFirmUploadStatus).
   */
  firmUpload: async (payload) => {
    try {
      const response = await api.post('/admin/utilities/firm-upload', payload);
      return response.data;
    } catch (error) {
      console.error('Error in firmUpload:', error);
      throw error;
    }
  },

  /**
   * Poll extraction / commit progress for a firm-upload workspace.
   */
  getFirmUploadStatus: async (workspaceId) => {
    try {
      const response = await api.get(`/admin/utilities/firm-upload-status/${workspaceId}`);
      return response.data;
    } catch (error) {
      console.error('Error in getFirmUploadStatus:', error);
      throw error;
    }
  },

  // ===== LOOKUP MAINTENANCE (DynamoDB lookup table via /admin/manage_lookups) =====

  getAllLookupTypes: async () => {
    const response = await api.get(MANAGE_LOOKUPS, {
      params: { format: 'admin' },
    });
    return response.data;
  },

  /**
   * @param {string} lookupType
   * @param {{ includeMetadata?: boolean }} [opts]
   */
  getLookupType: async (lookupType, opts = {}) => {
    const { includeMetadata = true } = opts;
    const response = await api.get(MANAGE_LOOKUPS, {
      params: {
        lookup_type: lookupType,
        format: 'admin',
        include_metadata: includeMetadata ? 'true' : 'false',
      },
    });
    return response.data;
  },

  createLookupType: async (lookupData) => {
    const response = await api.post(MANAGE_LOOKUPS, {
      lookup_type: lookupData.lookup_type,
      family: lookupData.family ?? 'custom',
      values: lookupData.values,
      modified_by: lookupData.modified_by ?? 'admin',
    });
    return response.data;
  },

  addLookupValue: async (lookupType, value) => {
    const response = await api.put(MANAGE_LOOKUPS, {
      lookup_type: lookupType,
      operation: 'add',
      values: [normalizeLookupValueForApi(value)],
      modified_by: 'admin',
    });
    return response.data;
  },

  updateLookupValue: async (lookupType, value) => {
    const response = await api.put(MANAGE_LOOKUPS, {
      lookup_type: lookupType,
      operation: 'update',
      updates: [normalizeLookupValueForApi(value)],
      modified_by: 'admin',
    });
    return response.data;
  },

  deleteLookup: async (lookupType, valueCode = null) => {
    const params = { lookup_type: lookupType };
    if (valueCode != null && valueCode !== '') {
      params.value = valueCode;
    }
    const response = await api.delete(MANAGE_LOOKUPS, { params });
    return response.data;
  },

  /**
   * Cross-table lookup sync (stage→prod copy or dev→stage merge).
   * @param {{ operation: 'copy_stage_to_prod'|'merge_dev_to_stage', dryRun?: boolean, lookupTypes?: string[], modifiedBy?: string }} opts
   */
  syncLookups: async ({ operation, dryRun = true, lookupTypes = null, modifiedBy = 'admin' }) => {
    const payload = {
      operation,
      dry_run: dryRun,
      modified_by: modifiedBy,
    };
    if (Array.isArray(lookupTypes) && lookupTypes.length > 0) {
      payload.lookup_types = lookupTypes;
    }
    const response = await api.put(MANAGE_LOOKUPS, payload);
    return response.data;
  },

  // ===== REV-SHARE MANAGER INVITES =====

  /**
   * Get a presigned S3 PUT URL for uploading a signed rev-share agreement PDF.
   * Upload directly to the URL with Content-Type: application/pdf,
   * then pass the returned s3_key to inviteRevShareManager().
   */
  /**
   * Normalize presigned-URL payload from GET /admin/utilities/rev-share-invite/upload-url
   * (supports flat, nested `data`, and camelCase keys).
   */
  getRevShareInviteUploadUrl: async (filename = 'signed-rev-share-agreement.pdf') => {
    const response = await api.get('/admin/utilities/rev-share-invite/upload-url', {
      params: { filename },
    });
    const root = response.data;
    let raw = root;
    if (raw && typeof raw === 'object' && raw.data != null && typeof raw.data === 'object') {
      raw = raw.data;
    }
    const upload_url = raw?.upload_url ?? raw?.uploadUrl;
    const s3_key = raw?.s3_key ?? raw?.s3Key;
    return { upload_url, s3_key };
  },

  /**
   * Resolve a manager by email for Existing manager offline attach.
   * @param {string} email
   * @returns {Promise<{ found: boolean, eligible: boolean, needs_firm: boolean, ineligible_reason?: string|null, manager?: object|null }>}
   */
  lookupRevShareManagerByEmail: async (email) => {
    const response = await api.get('/admin/utilities/rev-share-invite/lookup', {
      params: { email: (email || '').trim().toLowerCase() },
    });
    const root = response.data;
    if (root?.data && typeof root.data === 'object') return root.data;
    return root;
  },

  /**
   * Create a manager account for an offline rev-share agreement and send the invite email,
   * or attach an offline agreement to an existing manager (pass manager_user_id instead of email/name).
   * @param {object} data
   * @param {string} [data.manager_user_id] — existing manager path
   * @param {string} [data.firm_id] — existing manager with firm
   * @param {string} data.first_name — new manager path
   * @param {string} data.last_name
   * @param {string} data.email
   * @param {string} data.position
   * @param {string} data.agreement_s3_key — staged S3 key from getRevShareInviteUploadUrl
   * @param {string} data.admin_user_id
   * @param {Record<string, unknown>} [data.firm_data] — new manager or awaiting_firm existing manager
   * @param {string} [data.logo_temp_location]
   */
  inviteRevShareManager: async (data) => {
    const response = await api.post('/admin/utilities/rev-share-invite', data);
    return response.data;
  },

  /**
   * Queue async AI firm profile build (admin Lambda invokes generate_firm_profile_ai).
   * Call after firm workspace (e.g. team) is persisted on the client.
   * @param {{ firm_id: string, manager_user_id: string, admin_user_id: string }} data
   */
  triggerRevShareFirmProfile: async (data) => {
    const response = await api.post(
      '/admin/utilities/rev-share-invite/trigger-firm-profile',
      data,
    );
    return response.data;
  },

  /**
   * Resend the approval welcome email for a rev-share manager.
   * @param {{ user_id, admin_user_id }} data
   */
  resendRevShareInvite: async (data) => {
    const response = await api.post('/admin/utilities/rev-share-invite/resend', data);
    return response.data;
  },

  /**
   * List managers created via rev-share onboarding and their login/onboarding-oriented status.
   */
  listRevShareInvites: async () => {
    const response = await api.get('/admin/utilities/rev-share-invite');
    return response.data; // { invites: [...], count }
  },

  // ===== INVESTOR (ALLOCATOR) INVITES =====

  getInvestorInviteUploadUrl: async (filename = 'signed-accredited-declaration.pdf') => {
    const response = await api.get('/admin/utilities/investor-invite/upload-url', {
      params: { filename },
    });
    const root = response.data;
    let raw = root;
    if (raw && typeof raw === 'object' && raw.data != null && typeof raw.data === 'object') {
      raw = raw.data;
    }
    const upload_url = raw?.upload_url ?? raw?.uploadUrl;
    const s3_key = raw?.s3_key ?? raw?.s3Key;
    return { upload_url, s3_key };
  },

  /**
   * Create or complete an admin-provisioned investor invite.
   * Step 1: first_name, last_name, email, admin_user_id (creates account).
   * Step 2: user_id, institution_data, declaration_s3_key (staged PDF from getInvestorInviteUploadUrl).
   * @param {object} data
   * @param {string} data.first_name
   * @param {string} data.last_name
   * @param {string} data.email
   * @param {string} data.admin_user_id
   * @param {string} [data.user_id] — step 2 only
   * @param {string} [data.declaration_s3_key] — required on step 2; staged S3 key from upload-url
   * @param {Record<string, unknown>} [data.institution_data]
   * @param {string} [data.logo_temp_location]
   * @param {number} [data.selected_location_index]
   */
  inviteInvestor: async (data) => {
    const response = await api.post('/admin/utilities/investor-invite', data);
    return response.data;
  },

  resendInvestorInvite: async (data) => {
    const response = await api.post('/admin/utilities/investor-invite/resend', data);
    return response.data;
  },

  listInvestorInvites: async () => {
    const response = await api.get('/admin/utilities/investor-invite');
    return response.data;
  },

  // ===== ADMIN USER INVITES (add teammates as admins) =====

  listAdminInvites: async () => {
    const response = await api.get('/admin/utilities/admin-invite');
    return response.data;
  },

  inviteAdminUser: async (data) => {
    const response = await api.post('/admin/utilities/admin-invite', data);
    return response.data;
  },

  resendAdminInvite: async (userId) => {
    const response = await api.post('/admin/utilities/admin-invite/resend', { user_id: userId });
    return response.data;
  },

  setAdminUserStatus: async (userId, adminStatus) => {
    const response = await api.patch('/admin/utilities/admin-invite/status', {
      user_id: userId,
      admin_status: adminStatus,
    });
    return response.data;
  },

  displayToValue: (display) => displayToValue(display),

  generateValueColor: (value) => generateValueColor(value),

  createLookupValueTemplate: (order = 1) => ({
    value: '',
    display: '',
    description: '',
    active: true,
    order,
    color: '',
    associations: {},
  }),

  getLookupFamilyOptions: () => [
    { value: 'custom',         label: 'Custom' },
    { value: 'registration',   label: 'Registration' },
    { value: 'institution',    label: 'Institution' },
    { value: 'co_investments', label: 'Co-Investments' },
    { value: 'opportunities',  label: 'Opportunities' },
    { value: 'general',        label: 'General' },
  ],

  validateLookupValue: (value) => {
    const errors = [];
    if (!value || typeof value !== 'object') {
      errors.push('Value must be an object');
      return errors;
    }
    const required = ['value', 'display', 'description', 'active', 'order'];
    for (const field of required) {
      if (!(field in value)) {
        errors.push(`Missing required field: ${field}`);
      }
    }
    if (value.value != null && typeof value.value !== 'string') {
      errors.push('Field "value" must be a string');
    }
    if (value.display != null && typeof value.display !== 'string') {
      errors.push('Field "display" must be a string');
    }
    if (value.description != null && typeof value.description !== 'string') {
      errors.push('Field "description" must be a string');
    }
    if ('active' in value && typeof value.active !== 'boolean') {
      errors.push('Field "active" must be boolean');
    }
    if ('order' in value && typeof value.order !== 'number') {
      errors.push('Field "order" must be a number');
    }
    if ('associations' in value && value.associations != null && typeof value.associations !== 'object') {
      errors.push('Field "associations" must be an object');
    }
    return errors;
  },

  // ===== MARKETING EMAIL =====

  listMarketingTemplates: async () => {
    const response = await api.get('/admin/marketing/templates');
    return response.data;
  },

  getMarketingTemplate: async (templateId) => {
    const response = await api.get(`/admin/marketing/templates/${templateId}`);
    return response.data;
  },

  saveMarketingTemplate: async (templateId, payload) => {
    const response = await api.put(`/admin/marketing/templates/${templateId}`, payload);
    return response.data;
  },

  deleteMarketingTemplate: async (templateId) => {
    const response = await api.delete(`/admin/marketing/templates/${templateId}`);
    return response.data;
  },

  listMarketingCampaigns: async () => {
    const response = await api.get('/admin/marketing/campaigns');
    return response.data;
  },

  getMarketingCampaign: async (campaignId) => {
    const response = await api.get(`/admin/marketing/campaigns/${campaignId}`);
    return response.data;
  },

  deleteMarketingCampaign: async (campaignId) => {
    const response = await api.delete(`/admin/marketing/campaigns/${campaignId}`);
    return response.data;
  },

  createMarketingCampaign: async (payload) => {
    const response = await api.post('/admin/marketing/campaigns', payload);
    return response.data;
  },

  updateMarketingCampaign: async (campaignId, payload) => {
    const response = await api.patch(`/admin/marketing/campaigns/${campaignId}`, payload);
    return response.data;
  },

  sendMarketingCampaign: async (campaignId) => {
    const response = await api.post(`/admin/marketing/campaigns/${campaignId}/send`);
    return response.data;
  },

  testSendMarketingCampaign: async (campaignId, email) => {
    const response = await api.post(`/admin/marketing/campaigns/${campaignId}/test-send`, { email });
    return response.data;
  },

  getMarketingCampaignRecipients: async (campaignId, params = {}) => {
    const response = await api.get(`/admin/marketing/campaigns/${campaignId}/recipients`, { params });
    return response.data;
  },

  exportAllMarketingCampaignRecipients: async () => {
    const response = await api.get('/admin/marketing/campaigns/recipients/export', {
      timeout: 120000,
    });
    return response.data;
  },

  getMarketingNoActivityRecipients: async (campaignId, days = 5) => {
    const response = await api.get(`/admin/marketing/campaigns/${campaignId}/no-activity`, {
      params: { days },
    });
    return response.data;
  },

  listMarketingSuppression: async (params = {}) => {
    const response = await api.get('/admin/marketing/suppression', { params });
    return response.data;
  },

  checkMarketingSuppression: async (emails, audience) => {
    const response = await api.post('/admin/marketing/suppression/check', { emails, audience });
    return response.data;
  },

  addMarketingSuppression: async (payload) => {
    const response = await api.post('/admin/marketing/suppression/add', payload);
    return response.data;
  },

  checkMarketingRecipients: async (campaignId, emails) => {
    const response = await api.post(
      `/admin/marketing/campaigns/${campaignId}/check-recipients`,
      { emails },
      { timeout: 120000 },
    );
    return response.data;
  },

  generateMarketingHtmlBody: async (campaignId, payload) => {
    const response = await api.post(`/admin/marketing/campaigns/${campaignId}/generate-html-body`, payload);
    return response.data;
  },

  generateMarketingOpportunitiesPreview: async (payload) => {
    const response = await api.post(
      '/admin/marketing/opportunities/generate-preview',
      payload,
      { timeout: 120000 },
    );
    return response.data;
  },

  generateMarketingTemplateHtmlBody: async (templateId, payload) => {
    const response = await api.post(`/admin/marketing/templates/${templateId}/generate-html-body`, payload);
    return response.data;
  },

  // ===== MARKETING ANALYTICS =====

  getMarketingAnalytics: async (params = {}) => {
    const response = await api.get('/admin/marketing/analytics', { params });
    return response.data;
  },

  startMarketingAnalyticsMetrics: async (payload = {}) => {
    const response = await api.post('/admin/marketing/analytics/metrics', payload);
    return response.data;
  },

  getMarketingAnalyticsRecipients: async (params = {}) => {
    const response = await api.get('/admin/marketing/analytics/recipients', { params });
    return response.data;
  },

  getMarketingImportUploadUrl: async (payload) => {
    const response = await api.post('/admin/marketing/contacts/import/upload-url', payload);
    return response.data;
  },

  emailMarketingReport: async (payload) => {
    const response = await api.post('/admin/marketing/reports/email', payload);
    return response.data;
  },

  emailMarketingFollowUpReport: async (payload) => {
    const response = await api.post('/admin/marketing/reports/follow-ups/email', payload);
    return response.data;
  },

  // ===== MARKETING CONTACTS =====

  listMarketingContacts: async (params = {}) => {
    const response = await api.get('/admin/marketing/contacts', { params });
    return response.data;
  },

  getMarketingContact: async (id) => {
    const response = await api.get(`/admin/marketing/contacts/${id}`);
    return response.data;
  },

  createMarketingContact: async (payload) => {
    const response = await api.post('/admin/marketing/contacts', payload);
    return response.data;
  },

  updateMarketingContact: async (id, payload) => {
    const response = await api.patch(`/admin/marketing/contacts/${id}`, payload);
    return response.data;
  },

  deleteMarketingContact: async (id) => {
    const response = await api.delete(`/admin/marketing/contacts/${id}`);
    return response.data;
  },

  analyzeMarketingFile: async (payload) => {
    const response = await api.post('/admin/marketing/contacts/analyze-file', payload);
    return response.data;
  },

  parseMarketingAudience: async (payload) => {
    const response = await api.post('/admin/marketing/contacts/parse-audience', payload);
    return response.data;
  },

  // Returns { job_id, status: 'processing' } immediately — poll getJobStatus
  importMarketingContacts: async (payload) => {
    const response = await api.post('/admin/marketing/contacts/import-excel', payload);
    return response.data;
  },

  confirmMarketingContactsImport: async (payload) => {
    const response = await api.post('/admin/marketing/contacts/import-excel/confirm', payload);
    return response.data;
  },

  getContactCampaignHistory: async (id) => {
    const response = await api.get(`/admin/marketing/contacts/${id}/campaign-history`);
    return response.data;
  },

  getContactActivities: async (id) => {
    const response = await api.get(`/admin/marketing/contacts/${id}/activities`);
    return response.data;
  },

  createContactActivity: async (id, payload) => {
    const response = await api.post(`/admin/marketing/contacts/${id}/activities`, payload);
    return response.data;
  },

  updateContactActivity: async (contactId, activityId, payload) => {
    const response = await api.patch(
      `/admin/marketing/contacts/${contactId}/activities/${activityId}`,
      payload
    );
    return response.data;
  },

  getContactTimeline: async (id) => {
    const response = await api.get(`/admin/marketing/contacts/${id}/timeline`);
    return response.data;
  },

  listContactFollowUps: async (params = {}) => {
    const response = await api.get('/admin/marketing/contacts/follow-ups', { params });
    return response.data;
  },

  // Upserts CRM contacts for campaign/manual recipient lists.
  // Creates missing emails; fills empty profile fields when incoming data has values.
  batchEnsureMarketingContacts: async (contacts) => {
    const response = await api.post('/admin/marketing/contacts/batch-ensure', { contacts });
    return response.data;
  },

  // ===== SALES CRM ACTION CENTER =====

  getSalesActionCenter: async (params = {}) => {
    const response = await api.get('/admin/marketing/action-center', { params });
    return response.data;
  },

  // ===== SALES CRM ADMIN DIRECTORY (assign-to / @mentions) =====

  listCrmAdminUsers: async () => {
    const response = await api.get('/admin/marketing/admin-users');
    return response.data;
  },

  // ===== SALES CRM SAVED VIEWS =====

  listCrmSavedViews: async () => {
    const response = await api.get('/admin/marketing/contacts/saved-views');
    return response.data;
  },

  saveCrmSavedView: async (payload) => {
    const response = await api.post('/admin/marketing/contacts/saved-views', payload);
    return response.data;
  },

  deleteCrmSavedView: async (viewId) => {
    const response = await api.delete(`/admin/marketing/contacts/saved-views/${viewId}`);
    return response.data;
  },

  // ===== SALES CRM AUTOMATION (stall tasks + follow-up reminders) =====

  runCrmAutomation: async () => {
    const response = await api.post('/admin/marketing/automation/run');
    return response.data;
  },
};


export default adminService;